/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import axios from 'axios';
import { getRequiredEnvVar } from '@shared/utils/validate-env';
import { buildPortalIntegrationHeaders } from '@shared/utils/build-portal-integration-headers';
import { withAntiBotRetry } from '@shared/utils/with-antibot-retry';
import { FF_APP_OLX } from '@/shared/utils/feature-flag';

const API_BASE = getRequiredEnvVar('REACT_APP_API_URL');

export interface PortalSettings {
  agency_id: string;
  connected_portals: string[];
  enabled_portals: string[];
  auto_publish_active: boolean;
  auto_publish_portals: string[];
  olx_connected: boolean;
  olx_user_id?: string;
  /** Name/e-mail of the connected OLX account, shown as "Conectado como". */
  olx_user_name?: string | null;
  olx_user_email?: string | null;
  /** "invalid" once OLX rejected the stored token: the agency must reconnect. */
  olx_token_status?: 'valid' | 'invalid' | null;
  /** false right after connecting: an administrator must confirm the account before anything is sent. */
  olx_account_confirmed?: boolean | null;
  /** Version of the OLX publishing terms the agency accepted (with the account confirmation), or null. */
  olx_terms_version?: string | null;
  /** OLX plan check: "missing" blocks publishing until a plan is bought and re-checked. */
  olx_plan_status?: 'active' | 'missing' | 'unknown' | null;
  olx_plan_name?: string | null;
  olx_plan_ads_available?: number | null;
  olx_plan_ads_total?: number | null;
  /** When the plan's monthly ads come back (OLX ``next_renew_date``). */
  olx_plan_renews_at?: string | null;
  /** Why OLX cannot publish now (computed by the backend), or null. */
  olx_block_reason?: string | null;
  /** When the Canal Pro lead URL was generated (the URL itself is shown once). */
  canal_pro_lead_url_created_at?: string | null;
  last_vrsync_read_at?: string;
  /** When Chaves na Mão (not a browser) last read the private feed. */
  last_cnm_read_at?: string;
  /** When the private Chaves na Mão feed URL was generated (the URL itself is shown once). */
  cnm_feed_url_created_at?: string | null;
  /** Properties published on Chaves na Mão (the feed lists only these). */
  cnm_listed_ids?: string[];
  /** E-mail of the agency's Chaves na Mão account, once the lead token is saved. */
  cnm_lead_email?: string | null;
  cnm_lead_saved_at?: string | null;
}

/**
 * Version of the OLX publishing terms summarised in the confirmation pop-up
 * (the "Publicação de Anúncios na OLX" section of Terms of Service v5). Must
 * match ``OLX_TERMS_VERSION`` in the backend: bump both when the text changes.
 */
export const OLX_TERMS_VERSION = '2026-10-olx-v1';

/** Terms of Service version that holds the OLX section the pop-up links to. */
export const OLX_TERMS_URL = '/serviceterms/v5';

/**
 * Whether the connected OLX account still waits for the pop-up: the account
 * is unconfirmed, or the current OLX terms were never accepted (connections
 * made before the terms, or an older version).
 *
 * @param settings - Portal settings.
 * @returns True while the confirmation/acceptance is pending.
 */
export function olxAcceptancePending(settings: PortalSettings): boolean {
  if (!settings.olx_connected) return false;
  return settings.olx_account_confirmed === false || settings.olx_terms_version !== OLX_TERMS_VERSION;
}

/**
 * Builds the list of supported portals with localized labels.
 *
 * @param t - Translation function.
 * @returns Portal descriptors (id, label, connection method).
 */
export function getPortalLabels(t: (key: string) => string) {
  return [
    { id: 'olx', label: t('v2.portals.olx'), method: 'oauth' as const },
    { id: 'zap', label: t('v2.portals.zap'), method: 'xml' as const },
    { id: 'vivareal', label: t('v2.portals.vivareal'), method: 'xml' as const },
    { id: 'chavesnamao', label: t('v2.portals.chavesnamao'), method: 'xml-cnm' as const },
  ].filter((portal) => portal.id !== 'olx' || FF_APP_OLX);
}

/**
 * Builds the localized description map for each supported portal.
 *
 * @param t - Translation function.
 * @returns Map from portal id to its description.
 */
export function getPortalDescriptions(t: (key: string) => string): Record<string, string> {
  return {
    olx: t('v2.portals.descriptions.olx'),
    zap: t('v2.portals.descriptions.zap'),
    vivareal: t('v2.portals.descriptions.vivareal'),
    chavesnamao: t('v2.portals.descriptions.chavesnamao'),
  };
}

/**
 * Performs an authenticated, anti-bot-protected portal API request.
 *
 * @param path - The API path (appended to the base URL).
 * @param method - The HTTP method.
 * @param body - Optional request body.
 * @returns The parsed response data.
 */
export async function apiRequest(path: string, method: string, body?: unknown) {
  const res = await withAntiBotRetry(
    (headers) =>
      axios({
        url: `${API_BASE}${path}`,
        method: method.toLowerCase() as 'get' | 'post' | 'put' | 'patch' | 'delete',
        headers,
        data: body,
      }),
    (opts) => buildPortalIntegrationHeaders('portal_settings', opts),
  );
  return res.data;
}
